import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { Icon, SanitizeHtml } from '@aibees/shared/components';
import { ComingSoonTag } from '@aibees/shared/components/coming-soon-tag';
import { classnames } from '@aibees/shared/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { FilterTagList } from '../../components/filter-tags/components/filter-tag-list';
import { useFilterTags } from '../../components/filter-tags/hooks/use-filter-tags';
import { SearchFilter } from '../../types';
import { TextFieldFilter } from '../text-field-filter';
import type { TextFieldAccordionFilterProps as Props } from './types';

const POPPER_PROPS_DISABLE_PORTAL = { disablePortal: true };

export const TextFieldAccordionFilter = memo(
  ({ className, onChange, onOpen, ...filter }: Props) => {
    const { params } = useSearchQueryFilter({
      select: [filter.field!],
    });

    const tags = useFilterTags(params, filter as SearchFilter);

    return (
      <Accordion
        className={classnames('TextFieldAccordionFilter', className)}
        expanded={filter.open}
        onChange={() => onOpen?.(filter.field)}
        variant="filled"
        color="default"
      >
        <AccordionSummary
          disabled={filter.comingSoon}
          expandIcon={
            filter.comingSoon ? (
              <ComingSoonTag />
            ) : (
              <Icon
                name="chevron-down"
                size={16}
                color={filter.open ? 'primary.500' : 'grey.500'}
              />
            )
          }
        >
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body3" color="grey.600" fontWeight={600}>
              {filter.name}
            </Typography>
            {!filter.open && !!tags.tags?.length && (
              <FilterTagList items={tags.tags ?? []} />
            )}
          </Box>
          {filter.metadata?.info && !filter.comingSoon && (
            <Tooltip
              PopperProps={POPPER_PROPS_DISABLE_PORTAL}
              title={
                <SanitizeHtml html={(filter.metadata.info as string) ?? ''} />
              }
              arrow
              placement="bottom"
            >
              <Icon name="info" size={14} color="primary" />
            </Tooltip>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <TextFieldFilter onChange={onChange} {...filter} />
        </AccordionDetails>
      </Accordion>
    );
  }
);

TextFieldAccordionFilter.displayName = 'TextFieldAccordionFilter';
